import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, LayoutDashboard, Receipt, ScanLine, X,
  ArrowRight, ArrowLeft, CheckCircle2, Compass
} from "lucide-react";
import { useHelp } from "../../lib/help";

const TOUR_STEPS = [
  {
    step: 1,
    title: "Portfolio Dashboard",
    headline: "Your Real-Time Financial Command Center",
    description: "Track total portfolio valuation, net equity, monthly cash flow, and operating expense ratios at a glance with instant alerts for urgent repairs and overdue rent.",
    icon: LayoutDashboard,
    badge: "Step 1 of 3",
    path: "/",
    actionHint: "Overview & Metrics",
  },
  {
    step: 2,
    title: "Books & Transactions",
    headline: "IRS Schedule E Categorized Ledger",
    description: "Every dollar earned and spent is classified under official IRS tax categories. Easily track repairs, mortgage interest, utilities, and owner draws for stress-free tax filing.",
    icon: Receipt,
    badge: "Step 2 of 3",
    path: "/transactions",
    actionHint: "Income & Expenses",
  },
  {
    step: 3,
    title: "AI Receipt Scanner",
    headline: "Zero-Data-Entry with Gemini Flash OCR",
    description: "Snap or upload photos of receipts and invoices. Our AI extracts date, vendor, total amount, and line items so you can exclude personal items with a single tap.",
    icon: ScanLine,
    badge: "Step 3 of 3",
    path: "/scan",
    actionHint: "Instant Receipt Analysis",
  },
];

export default function FeatureTour() {
  const { isTourOpen, closeTour, tourStepIndex, setTourStepIndex } = useHelp();
  const currentStepIndex = Math.min(Math.max(0, tourStepIndex || 0), TOUR_STEPS.length - 1);
  const setCurrentStepIndex = setTourStepIndex;
  const navigate = useNavigate();

  // Dismiss on Escape key
  useEffect(() => {
    if (!isTourOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeTour();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isTourOpen, closeTour]);

  if (!isTourOpen) return null;


  const current = TOUR_STEPS[currentStepIndex];
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === TOUR_STEPS.length - 1;
  const StepIcon = current.icon;

  const handleNext = () => {
    if (isLast) {
      closeTour();
      navigate("/");
    } else {
      const nextIdx = currentStepIndex + 1;
      setCurrentStepIndex(nextIdx);
      if (TOUR_STEPS[nextIdx]?.path) {
        navigate(TOUR_STEPS[nextIdx].path);
      }
    }
  };

  const handleBack = () => {
    if (!isFirst) {
      const prevIdx = currentStepIndex - 1;
      setCurrentStepIndex(prevIdx);
      if (TOUR_STEPS[prevIdx]?.path) {
        navigate(TOUR_STEPS[prevIdx].path);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeTour();
        }
      }}
    >
      <div
        className="relative w-full max-w-lg rounded-3xl bg-[#13131A] border border-[#1E1E2E] shadow-2xl p-6 sm:p-8 overflow-hidden animate-in zoom-in-95 duration-200"
        data-testid="feature-tour-modal"
      >
        {/* Glow ambient background */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-teal-500/15 rounded-full blur-3xl pointer-events-none" />

        {/* Top controls: Badge & Skip Button */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="tag bg-teal-500/15 text-teal-400 border border-teal-500/30">
              {current.badge}
            </span>
            <span className="text-xs text-[var(--muted)] font-semibold">
              Quick Feature Tour
            </span>
          </div>

          <button
            onClick={closeTour}
            data-testid="tour-skip-button"
            className="text-xs font-semibold text-[var(--muted)] hover:text-white flex items-center gap-1 p-1.5 rounded-lg hover:bg-white/5 transition-colors"
          >
            <span>Skip tour</span>
            <X size={14} />
          </button>
        </div>

        {/* Center illustration / Icon */}
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center shadow-lg mb-5 mx-auto">
          <StepIcon size={30} />
        </div>

        {/* Step Content */}
        <div className="text-center mb-8">
          <div className="text-xs uppercase font-bold tracking-widest text-teal-400 mb-1">
            {current.title}
          </div>
          <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight mb-3">
            {current.headline}
          </h3>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed max-w-md mx-auto">
            {current.description}
          </p>
        </div>

        {/* Step dots */}
        <div className="flex justify-center items-center gap-1.5 mb-6">
          {TOUR_STEPS.map((s, idx) => (
            <button
              key={s.step}
              onClick={() => {
                setCurrentStepIndex(idx);
                navigate(s.path);
              }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                idx === currentStepIndex
                  ? "w-8 bg-teal-400"
                  : "w-2 bg-[var(--border)] hover:bg-[var(--muted)]"
              }`}
              aria-label={`Go to step ${s.step}`}
            />
          ))}
        </div>

        {/* Bottom Actions */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-[#1E1E2E]">
          <button
            type="button"
            onClick={handleBack}
            disabled={isFirst}
            className={`btn-secondary text-xs py-2 px-3 flex items-center gap-1 ${
              isFirst ? "opacity-0 pointer-events-none" : ""
            }`}
          >
            <ArrowLeft size={13} />
            <span>Back</span>
          </button>

          <button
            type="button"
            onClick={handleNext}
            data-testid="tour-next-button"
            className="btn-primary text-xs py-2.5 px-5 flex items-center gap-1.5 ml-auto"
          >
            <span>{isLast ? "Finish & Start Using App" : "Next Feature"}</span>
            {isLast ? <CheckCircle2 size={14} /> : <ArrowRight size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}
